import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../common/Loader.jsx';
import ResponseErrorMessage from '../common/ResponseErrorMessage.jsx';
import { fetchTp3, kindGroups, standardHandle } from './standardsApi.js';

/**
 * The Shmoop content aligned to one standard, grouped by kind
 *
 * Drills, practice tests and diagnostics are listed from their questions' alignments,
 * so each shows "N of M questions". Readings are aligned directly.
 */
const StandardContent = props => {
	const { standardUuid, onForbidden } = props;
	const [content, setContent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const abortController = new AbortController();

		setLoading(true);
		setError(null);
		fetchTp3('GetTestPrepStandardContent', { uuid: standardUuid }, abortController.signal)
			.then(payload => setContent(payload))
			.catch(e => {
				if (abortController.signal.aborted) {
					return;
				}
				if (e.status === 403) {
					onForbidden();
				} else {
					setError(e);
				}
			})
			.finally(() => !abortController.signal.aborted && setLoading(false));

		return () => abortController.abort();
	}, [standardUuid]);

	if (loading) {
		return <Loader />;
	}
	if (error !== null) {
		return <ResponseErrorMessage error={error} />;
	}

	const { standard, items } = content;
	const breadcrumb = standard.breadcrumb.map(crumb => standardHandle(crumb) || crumb.statement).join(' › ');

	return (
		<div className={'standard-content'}>
			<p className={'standard-content-context'}>
				{ standard.authorityLabel } · { standard.frameworkTitle }{ breadcrumb !== '' && ' › ' + breadcrumb }
			</p>
			<h3 className={'standard-content-statement'}>
				{ standardHandle(standard) !== '' && <strong>{ standardHandle(standard) } </strong> }
				{ standard.statement }
			</h3>
			<p className={'sr-only'} aria-live={'polite'}>
				{ items.length } { items.length === 1 ? 'item' : 'items' } aligned to this standard
			</p>
			{
				items.length === 0 ?
					<div className={'error-message-box'}>
						<i className={'fas fa-info-circle'} aria-hidden="true"></i> No Shmoop content is aligned to
						this standard yet.
					</div> :
					kindGroups
						.filter(group => items.some(item => item.kind === group.kind))
						.map(group =>
							<div key={group.kind} className={'standard-content-group'}>
								<h4>{ group.title }</h4>
								<ul className={'standard-content-list'}>
									{
										items.filter(item => item.kind === group.kind).map(item =>
											<li key={item.uuid} className={'d-flex'}>
												<div className={'dark-bullet flex-shrink-0 mt-1 mr-2'}></div>
												<div>
													<a href={item.url}>{ item.title }</a>
													<span className={'standard-content-meta'}>
														{ item.familyName }
														{
															item.alignedQuestions !== null &&
																' · ' + item.alignedQuestions + ' of ' +
																item.totalQuestions + ' questions'
														}
													</span>
												</div>
											</li>
										)
									}
								</ul>
							</div>
						)
			}
		</div>
	);
};

StandardContent.propTypes = {
	standardUuid: PropTypes.string.isRequired,
	onForbidden: PropTypes.func.isRequired
};

export default StandardContent;
